import { useCallback, useContext, useEffect, useState } from 'react';

import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import Image from 'next/image';
import LogRocket from '@providers/LogRocket';
import Spacer from '@components/Spacer';
import { NoodleProductTypes } from '@typings/graphql-models';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { EmptyLayoutZeroPadding } from '@layouts';
import { Layout } from '@typings/NextJS';
import ProductIcon from '@components/ProductIcon';
import Buttons from '@components/Buttons/Buttons';
import { useRouter } from 'next/router';
import { mixpanelTrack } from '@providers/Mixpanel';
import CustomLink from '@components/CustomLink';
import { ToastTypeVariants } from '@context/ToastContext';
import BackButton from '@components/BackButton';
import { useIsMobile, useUser } from '@hooks';
import deserializeQueryStringItem from '@helpers/deserializeQueryStringItem';
import teamsContext from '@providers/Teams/TeamsContext';
import X from '@components/Icons/X';
import AppSumoLogo from '@components/Icons/AppSumo';
import s from './Layout.module.scss';
import OnboardingContext from './Context';

type GetCreatorProfileResponse = Awaited<ReturnType<typeof tsClient.creators.getCreatorProfile>>;

const PRODUCT_TYPE_NAMES: Record<string, string> = {
  [NoodleProductTypes.Lite]: 'Paid Chat',
  [NoodleProductTypes.InPersonSession]: 'In Person Service',
  [NoodleProductTypes.OnlineSession]: 'Online Service',
};

const OnboardingLayout: Layout = ({ children }) => {
  const {
    fetchingState: { isFetching: isFetchingProducts },
    getData: getCreatorProductsFn,
  } = useNoodleApi(tsClient.getCreatorProducts, {
    errorFlow: 'onboarding',
  });
  const { data: fetchedCreator, getData: getCreatorProfileFn } = useNoodleApi(tsClient.creators.getCreatorProfile, {
    errorFlow: 'onboarding',
    toastErrorMessage: () => [ToastTypeVariants.ERROR, 'Failed to get creator profile'],
    toastOnError: true,
  });
  const [user] = useUser();
  const [creator, setCreator] = useState<GetCreatorProfileResponse | null>(null);
  const [products, setProducts] = useState<Awaited<ReturnType<typeof tsClient.getCreatorProducts>>['products']>([]);
  const [backButtonAction, setBackButtonAction] = useState<(() => void) | null>(null);
  const [nextAction, setNextAction] = useState<(() => void) | null>(null);
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState<boolean>(false);
  const [activeProductType, setActiveProductType] = useState<NoodleProductTypes | null>(null);
  const router = useRouter();
  const isMobile = useIsMobile();

  const { creatorId, refetchTeams } = useContext(teamsContext);

  const vendor = deserializeQueryStringItem(router.query.vendor, { type: 'string' });
  const isAgency = deserializeQueryStringItem(router.query.isAgency, { type: 'boolean' });

  useEffect(() => {
    if (creatorId && user?.id && user.isCreator && !user.isAnonymous) {
      getCreatorProfileFn({ creatorId });
    } else {
      setCreator(null);
    }
  }, [creatorId, user, getCreatorProfileFn]);

  useEffect(() => {
    if (fetchedCreator) {
      setCreator(fetchedCreator);
    } else {
      setCreator(null);
    }
  }, [fetchedCreator]);

  const getData = useCallback(async (): Promise<Awaited<ReturnType<typeof tsClient.getCreatorProducts>>['products']> => {
    if (creator?.slug) {
      const { data } = await getCreatorProductsFn({ creatorSlug: creator?.slug });
      setProducts(data?.products || []);
      return data?.products || [];
    }
    return [];
  }, [getCreatorProductsFn, creator?.slug]);

  const handleCompleteOnboarding = useCallback(async (): Promise<void> => {
    setIsCompletingOnboarding(true);
    if (nextAction) {
      nextAction();
    }
    mixpanelTrack('Completed services review', {
      creatorSlug: creator?.slug,
    });
    await refetchTeams();
    await router.push(getUrl(IDENTIFIERS.AGENCY_DASHBOARD));
  }, [nextAction, creator, refetchTeams, router]);

  useEffect(() => {
    getData();
  }, [getData]);

  const isClaimingSlug = router.asPath.includes(getUrl(IDENTIFIERS.CREATOR_ONBOARDING_CLAIM_SLUG));
  const isAgencyOnboarding = router.asPath.includes(getUrl(IDENTIFIERS.AGENCY_ONBOARDING));
  const isCreatorSignup = router.asPath.includes(getUrl(IDENTIFIERS.CREATOR_SIGN_UP, { slug: '' }));
  const isVendorAppsumo = vendor === 'appsumo';

  return (
    <EmptyLayoutZeroPadding>
      <OnboardingContext.Provider
        value={{
          creator,
          handleCompleteOnboarding,
          isFetchingProducts,
          products,
          refetchProducts: getData,
          setActiveProductType,
          setBackButtonAction,
          setNextAction,
        }}
      >
        <LogRocket />
        <div className={s.contentWrapper}>
          <div className={s.header}>
            {backButtonAction && !isCreatorSignup
              ? (
                <BackButton className={s.backLink} backAction={backButtonAction} title="" />
              )
              : (
                <CustomLink
                  className={isClaimingSlug || isAgencyOnboarding ? s.headerLogoClaimingSlug : s.headerLogo}
                  to={getUrl(IDENTIFIERS.HOME)}
                  style={{ minWidth: isMobile ? 76 : 160 }}
                >
                  <Image src="/full-glade-logo.png" width={96} height={21} alt="Glade AI" style={{ position: 'relative', top: 3 }} />
                  {isVendorAppsumo && (
                    <>
                      <X weight="bold" color="var(--color-gray-100)" />
                      <AppSumoLogo />
                    </>
                  )}
                </CustomLink>
              )}
            {activeProductType && (
              <div className={s.title}>
                <p className="caption">Review your services</p>
                <div className={s.productType}>
                  <ProductIcon size={12} noodleProductType={activeProductType} />
                  <p className="body-md-bold">{PRODUCT_TYPE_NAMES[activeProductType]}</p>
                </div>
              </div>
            )}
            {nextAction && (
              <>
                {isMobile && <div style={{ width: 72 }} />}
                <Buttons
                  isWrapper
                  className={s.nextButton}
                  style={(!isMobile && { bottom: 'unset', position: 'relative', width: 'unset' }) || undefined}
                  onClick={
                    isAgency
                      ? handleCompleteOnboarding
                      : () => {
                        mixpanelTrack('Completed services review', {
                          creatorSlug: creator?.slug,
                        });
                        router.push(getUrl(IDENTIFIERS.CREATOR_ONBOARDING_SUCCESS));
                      }
                  }
                  isShimmering={isCompletingOnboarding}
                >
                  {isAgency ? 'Go back to Dashboard' : 'Finish onboarding'}
                </Buttons>
              </>
            )}
            {(isClaimingSlug || isAgencyOnboarding) && (
              <CustomLink to={getUrl(IDENTIFIERS.HOME)} className={s.learnMore} newTab>
                Learn more
              </CustomLink>
            )}
            {!nextAction && !isClaimingSlug && !isAgencyOnboarding && <div style={{ minWidth: isMobile ? 76 : 160 }} />}
          </div>
          <div className={s.content}>
            <div className={s.content__main}>{children}</div>
          </div>
          <Spacer size={96} />
        </div>
      </OnboardingContext.Provider>
    </EmptyLayoutZeroPadding>
  );
};

export default OnboardingLayout;
